import React, { useEffect, useMemo } from 'react'
import cx from 'classnames'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { List, Lozenge, l, SecondaryNavigation } from '@manychat/manyui'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { AiTitle } from 'apps/aiAutomation/components/AiTitle'
import { EngagementGoalEditor } from 'apps/aiAutomation/components/EngagementGoalEditor'
import { GoalTypePicker } from 'apps/aiAutomation/components/GoalTypePicker'
import { InstantAnswers } from 'apps/aiAutomation/components/InstantAnswers'
import { KnowledgeAdded } from 'apps/aiAutomation/components/KnowledgeAdded'
import { KnowledgeEntryTypePicker } from 'apps/aiAutomation/components/KnowledgeEntryTypePicker'
import { Layout } from 'apps/aiAutomation/components/Layout'
import { Content } from 'apps/aiAutomation/components/Layout/components/Content'
import { Sidebar } from 'apps/aiAutomation/components/Layout/components/Sidebar'
import { SidebarView } from 'apps/aiAutomation/components/Layout/components/SidebarView'
import { PositiveCommentReplies } from 'apps/aiAutomation/components/PositiveCommentReplies'
import { TovSetup } from 'apps/aiAutomation/components/TovSetup'
import { SidebarKnowledgeEditor } from 'apps/aiAutomation/layout/SidebarKnowledgeEditor'
import { AI_AUTOMATION_NAVIGATION_CONFIG } from 'apps/aiAutomation/lib/navigation'
import { SidebarViewType } from 'apps/aiAutomation/lib/sidebar'
import { useAiAutomationCurrentRoute } from 'apps/aiAutomation/pages/hooks/useAiAutomationCurrentRoute'
import { aiAutomationResetAll } from 'apps/aiAutomation/store/actions'
import { AiAutomationBehaviorActions } from 'apps/aiAutomation/store/behavior'
import { AiAlwaysOnAutomationsActions } from 'apps/aiAutomation/store/core'
import { AiAutomationEngagementGoalActions } from 'apps/aiAutomation/store/engagementGoal'
import { AiAutomationKnowledgeActions } from 'apps/aiAutomation/store/knowledge'
import { AiAutomationLayoutSelectors } from 'apps/aiAutomation/store/layout'
import { AiAutomationStatsActions } from 'apps/aiAutomation/store/stats'
import { ErrorBoundary } from 'common/core/components/ErrorBoundary'
import Page from 'common/core/components/Page/Page'
import { useMediaQuery } from 'utils/commonHooks'
import { typedRouting } from 'utils/router'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'
import { useAccountReleaseToggle } from 'utils/services/featureFlagsService/releaseTogglesHooks'

import cm from './PageLayout.module.css'

interface PageLayoutProps {
  children: React.ReactNode
}

export const PageLayout = ({ children }: PageLayoutProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const isSidebarOpen = useAppSelector(AiAutomationLayoutSelectors.getIsSidebarOpen)
  const isGradientBackground = useAppSelector(AiAutomationLayoutSelectors.getIsGradientBackground)
  const isBehaviorPageEnabled = useAccountReleaseToggle(AccountFlag.ENABLE_AI_BEHAVIOR_PAGE)
  const visibleNavItems = useMemo(() => {
    return AI_AUTOMATION_NAVIGATION_CONFIG.filter(({ route }) => {
      return !(route === 'behavior' && !isBehaviorPageEnabled)
    })
  }, [isBehaviorPageEnabled])

  const currentRoute = useAiAutomationCurrentRoute()

  const isMobile = useMediaQuery('(max-width: 600px)')

  const currentNavItem = AI_AUTOMATION_NAVIGATION_CONFIG.find((item) => item.route === currentRoute)

  const handleBackClick = () => navigate(typedRouting.linkBuilder.aiAutomation())

  useEffect(() => {
    dispatch(AiAutomationKnowledgeActions.getListFx())
    dispatch(AiAlwaysOnAutomationsActions.getConfigFx())
    dispatch(AiAutomationEngagementGoalActions.getListFx())
    dispatch(AiAutomationStatsActions.getStatsFx())

    if (isBehaviorPageEnabled) {
      dispatch(AiAutomationBehaviorActions.getConfigFx())
    }

    return () => {
      dispatch(aiAutomationResetAll())
    }
  }, [dispatch, isBehaviorPageEnabled])

  return (
    <ErrorBoundary scope="ai" section="ai_always_on_automation">
      <Page
        title={<AiTitle />}
        contentClassName={cx(cm.page, isGradientBackground && cm.page_gradient)}
        hideMobileWarning
      >
        <Layout>
          <div className={cx(cm.pageLayout, !currentRoute && cm.pageLayout_fullscreen)}>
            <SecondaryNavigation
              className={cx(
                cm.nav,
                isSidebarOpen && cm.nav_muted,
                !currentRoute && cm.nav_fullscreen,
              )}
              ariaLabel={l.translate('Manychat AI navigation')}
              selectedItemLabel={isMobile ? currentNavItem?.label : undefined}
              onBackClick={handleBackClick}
              menuContent={
                <List role="menu">
                  {visibleNavItems.map((item) => (
                    <SecondaryNavigation.Item<typeof RouterLink>
                      key={item.route}
                      as={RouterLink}
                      to={item.to()}
                      icon={item.icon}
                      ariaLabel={item.label}
                      selected={!isMobile && currentRoute === item.route}
                    >
                      {item.label}
                      {item.badge && <Lozenge className="m-l-sm" size="medium" type={item.badge} />}
                    </SecondaryNavigation.Item>
                  ))}
                </List>
              }
            />

            <Content
              className={cm.pageLayoutContent}
              isWide={currentRoute !== null && ['training', 'behavior'].includes(currentRoute)}
            >
              {children}
            </Content>
          </div>

          <Sidebar>
            <SidebarView viewId={SidebarViewType.KNOWLEDGE_EDITOR}>
              <SidebarKnowledgeEditor />
            </SidebarView>

            <SidebarView viewId={SidebarViewType.INSTANT_ANSWERS}>
              <InstantAnswers />
            </SidebarView>

            <SidebarView viewId={SidebarViewType.POSITIVE_COMMENT_REPLIES}>
              <PositiveCommentReplies />
            </SidebarView>

            <SidebarView viewId={SidebarViewType.TOV_SETUP}>
              <TovSetup />
            </SidebarView>

            <SidebarView viewId={SidebarViewType.KNOWLEDGE_ADDED}>
              <KnowledgeAdded />
            </SidebarView>

            <SidebarView viewId={SidebarViewType.ENGAGEMENT_GOAL_EDITOR}>
              <EngagementGoalEditor />
            </SidebarView>

            <SidebarView viewId={SidebarViewType.GOAL_TYPE_PICKER}>
              <GoalTypePicker />
            </SidebarView>

            <SidebarView viewId={SidebarViewType.KNOWLEDGE_ENTRY_TYPE_PICKER}>
              <KnowledgeEntryTypePicker />
            </SidebarView>
          </Sidebar>
        </Layout>
      </Page>
    </ErrorBoundary>
  )
}
